import { useState, useEffect, useCallback, useMemo } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { useParams } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format, parse, startOfDay, addDays } from "date-fns";
import {
  Calendar as CalendarIcon,
  Users,
  Search,
  ArrowLeft,
  BedDouble,
  Clock,
  CheckCircle,
  CreditCard,
  MapPin,
  Star,
  ChevronLeft,
  ChevronRight,
  Plus,
  Minus,
  ShoppingBag,
  Coffee,
  Car,
  Compass,
  Tag,
} from "lucide-react";
import { cn } from "@/lib/utils";

type BookingStep = "search" | "results" | "details" | "addons" | "payment";

interface ApartmentPhotoData {
  id: number;
  url: string;
  caption?: string;
  sortOrder: number;
  isCover: boolean;
}

interface UpsellItemData {
  id: number;
  name: string;
  description?: string;
  price: string;
  currency: string;
  category: string;
  isActive: boolean;
}

interface PageData {
  page: {
    id: number;
    slug: string;
    title: string;
    description?: string;
    isActive: boolean;
    primaryColor?: string;
    minStay?: number;
    maxStay?: number;
    heroImage?: string;
    organizationName?: string;
  };
  ratePlans: Array<{
    id: number;
    name: string;
    baseNightlyRate: string;
    cleaningFee?: string;
    weekendRate?: string;
    weeklyDiscount?: string;
    monthlyDiscount?: string;
    apartmentId: number;
  }>;
  apartments: Array<{
    id: number;
    name: string;
    slug: string;
    heroImage?: string;
    bedrooms?: number;
    photos?: ApartmentPhotoData[];
  }>;
  apartmentPhotos?: Record<number, ApartmentPhotoData[]>;
  upsellItems?: UpsellItemData[];
}

interface SearchResult {
  ratePlan: PageData["ratePlans"][0];
  apartment: PageData["apartments"][0] & { photos?: ApartmentPhotoData[] };
  totalAmount: string;
  totalNightly: string;
  cleaningFee: string;
  nights: number;
  currency: string;
  taxRate: string;
  taxName: string;
  taxAmount: string;
  totalPrice?: number;
  priceBreakdown?: {
    nightlyTotal?: number;
    cleaningFee?: number;
    weekendSurcharge?: number;
    discount?: number;
  };
}

interface HoldData {
  id: number;
  checkIn: string;
  checkOut: string;
  totalAmount: number;
  expiresAt: string;
  status: string;
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

function formatCurrency(amount: number | string) {
  const num = typeof amount === "string" ? parseFloat(amount) : amount;
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(num);
}

function getDefaultCheckout(checkIn: string) {
  const d = new Date(checkIn);
  d.setDate(d.getDate() + 2);
  return d.toISOString().split("T")[0];
}

function getPhotosForApartment(
  apartment: SearchResult["apartment"],
  apartmentPhotos?: Record<number, ApartmentPhotoData[]>,
): ApartmentPhotoData[] {
  if (apartment.photos && apartment.photos.length > 0) {
    const sorted = [...apartment.photos].sort((a, b) => {
      if (a.isCover && !b.isCover) return -1;
      if (!a.isCover && b.isCover) return 1;
      return a.sortOrder - b.sortOrder;
    });
    return sorted;
  }
  if (apartmentPhotos && apartmentPhotos[apartment.id] && apartmentPhotos[apartment.id].length > 0) {
    const sorted = [...apartmentPhotos[apartment.id]].sort((a, b) => {
      if (a.isCover && !b.isCover) return -1;
      if (!a.isCover && b.isCover) return 1;
      return a.sortOrder - b.sortOrder;
    });
    return sorted;
  }
  return [];
}

function getCategoryIcon(category: string) {
  switch (category) {
    case "early_checkin":
      return <Clock className="w-3 h-3" />;
    case "late_checkout":
      return <Clock className="w-3 h-3" />;
    case "transport":
      return <Car className="w-3 h-3" />;
    case "experience":
      return <Compass className="w-3 h-3" />;
    case "amenity":
      return <Coffee className="w-3 h-3" />;
    default:
      return <Tag className="w-3 h-3" />;
  }
}

function getCategoryLabel(category: string) {
  switch (category) {
    case "early_checkin":
      return "Early Check-in";
    case "late_checkout":
      return "Late Checkout";
    case "transport":
      return "Transport";
    case "experience":
      return "Experience";
    case "amenity":
      return "Amenity";
    default:
      return "Add-on";
  }
}

function PhotoGallery({
  photos,
  fallbackImage,
  altText,
  aspectClass = "aspect-[16/10]",
  testIdPrefix = "gallery",
}: {
  photos: ApartmentPhotoData[];
  fallbackImage?: string;
  altText: string;
  aspectClass?: string;
  testIdPrefix?: string;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);

  const hasPhotos = photos.length > 0;
  const imageUrl = hasPhotos ? photos[currentIndex]?.url : fallbackImage;

  if (!imageUrl) return null;

  const goNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasPhotos) setCurrentIndex((prev) => (prev + 1) % photos.length);
  };

  const goPrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasPhotos) setCurrentIndex((prev) => (prev - 1 + photos.length) % photos.length);
  };

  return (
    <div className={`relative ${aspectClass} overflow-hidden rounded-md group`} data-testid={`${testIdPrefix}-container`}>
      <img
        src={imageUrl}
        alt={hasPhotos && photos[currentIndex]?.caption ? photos[currentIndex].caption : altText}
        className="w-full h-full object-cover"
        data-testid={`${testIdPrefix}-image`}
      />
      {hasPhotos && photos.length > 1 && (
        <>
          <button
            onClick={goPrev}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid={`${testIdPrefix}-prev`}
          >
            <ChevronLeft className="w-4 h-4 text-gray-800" />
          </button>
          <button
            onClick={goNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid={`${testIdPrefix}-next`}
          >
            <ChevronRight className="w-4 h-4 text-gray-800" />
          </button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5" data-testid={`${testIdPrefix}-dots`}>
            {photos.map((_, i) => (
              <button
                key={i}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentIndex(i);
                }}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${
                  i === currentIndex ? "bg-white" : "bg-white/50"
                }`}
                data-testid={`${testIdPrefix}-dot-${i}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function PublicBookingPage() {
  const params = useParams<{ orgSlug: string; pageSlug: string }>();
  const orgSlug = params.orgSlug || "";
  const pageSlug = params.pageSlug || "";

  const urlSearchParams = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
  const prefillCheckIn = urlSearchParams.get("checkIn") || "";
  const prefillCheckOut = urlSearchParams.get("checkOut") || "";
  const prefillApartmentId = urlSearchParams.get("apartmentId") ? parseInt(urlSearchParams.get("apartmentId")!) : null;
  const prefillRatePlanId = urlSearchParams.get("ratePlanId") ? parseInt(urlSearchParams.get("ratePlanId")!) : null;
  const prefillRef = urlSearchParams.get("ref") || "";
  const prefillSource = urlSearchParams.get("source") || "";

  const [step, setStep] = useState<BookingStep>(prefillCheckIn && prefillCheckOut ? "results" : "search");
  const [checkIn, setCheckIn] = useState(prefillCheckIn);
  const [checkOut, setCheckOut] = useState(prefillCheckOut);
  const [guests, setGuests] = useState(2);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedResult, setSelectedResult] = useState<SearchResult | null>(null);
  const [hold, setHold] = useState<HoldData | null>(null);
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [countdown, setCountdown] = useState("");
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [upsellSelections, setUpsellSelections] = useState<Array<{ itemId: number; quantity: number }>>([]);
  const [autoSearchDone, setAutoSearchDone] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [connectedAccountId, setConnectedAccountId] = useState<string | null>(null);
  const [paymentSetupError, setPaymentSetupError] = useState<string | null>(null);

  const { data: pageData, isLoading: pageLoading, error: pageError } = useQuery<PageData>({
    queryKey: ["/api/public/booking", orgSlug, pageSlug],
    enabled: !!orgSlug && !!pageSlug,
  });

  const primaryColor = pageData?.page?.primaryColor || "#FF385C";
  const upsellItems = (pageData?.upsellItems || []).filter((item) => item.isActive);
  const hasUpsells = upsellItems.length > 0;

  const searchMutation = useMutation({
    mutationFn: async (body: { checkIn: string; checkOut: string; guests: number }) => {
      const res = await apiRequest("POST", `/api/public/booking/${orgSlug}/${pageSlug}/search`, body);
      return res.json();
    },
  });

  const holdMutation = useMutation({
    mutationFn: async (body: { ratePlanId: number; checkIn: string; checkOut: string; guestName?: string; guestEmail?: string }) => {
      const res = await apiRequest("POST", "/api/public/booking/hold", body);
      return res.json();
    },
    onSuccess: (data: HoldData) => {
      setHold(data);
      setStep("details");
    },
  });

  const checkoutMutation = useMutation({
    mutationFn: async (body: { holdId: number; guestName: string; guestEmail: string; guestPhone?: string; upsellSelections?: Array<{ itemId: number; quantity: number }>; source?: string }) => {
      const res = await apiRequest("POST", "/api/public/booking/checkout", body);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Payment setup failed");
      }
      return res.json() as Promise<{ clientSecret: string; connectedAccountId: string; holdId: number }>;
    },
    onSuccess: (data) => {
      setClientSecret(data.clientSecret);
      setConnectedAccountId(data.connectedAccountId);
      setPaymentSetupError(null);
    },
    onError: (err: Error) => {
      setPaymentSetupError(err.message);
    },
  });

  const confirmMutation = useMutation({
    mutationFn: async (body: { holdId: number; paymentIntentId: string }) => {
      const res = await apiRequest("POST", "/api/public/booking/confirm", body);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Booking confirmation failed");
      }
      return res.json();
    },
    onSuccess: () => {
      setBookingConfirmed(true);
    },
  });

  const updateCountdown = useCallback(() => {
    if (!hold?.expiresAt) return;
    const now = new Date().getTime();
    const expires = new Date(hold.expiresAt).getTime();
    const diff = expires - now;
    if (diff <= 0) {
      setCountdown("Expired");
      return;
    }
    const mins = Math.floor(diff / 60000);
    const secs = Math.floor((diff % 60000) / 1000);
    setCountdown(`${mins}:${secs.toString().padStart(2, "0")}`);
  }, [hold?.expiresAt]);

  useEffect(() => {
    if ((step !== "details" && step !== "addons") || !hold) return;
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [step, hold, updateCountdown]);

  useEffect(() => {
    if (prefillRef) {
      sessionStorage.setItem("staio_ref", prefillRef);
    }
  }, [prefillRef]);

  useEffect(() => {
    if (autoSearchDone || !prefillCheckIn || !prefillCheckOut || !pageData || !orgSlug || !pageSlug) return;
    setAutoSearchDone(true);
    searchMutation.mutate(
      { checkIn: prefillCheckIn, checkOut: prefillCheckOut, guests },
      {
        onSuccess: (data: { results?: SearchResult[]; available?: SearchResult[] }) => {
          const searchResults = data.results || data.available || [];
          setResults(searchResults);
          if (prefillRatePlanId || prefillApartmentId) {
            const matchedResult = searchResults.find(r =>
              (prefillRatePlanId && r.ratePlan.id === prefillRatePlanId) ||
              (prefillApartmentId && r.apartment.id === prefillApartmentId)
            );
            if (matchedResult) {
              setSelectedResult(matchedResult);
              holdMutation.mutate({
                ratePlanId: matchedResult.ratePlan.id,
                checkIn: prefillCheckIn,
                checkOut: prefillCheckOut,
              });
              return;
            }
          }
          setStep("results");
        },
      }
    );
  }, [pageData, autoSearchDone]);

  const handleSearch = () => {
    if (!checkIn || !checkOut) return;
    searchMutation.mutate(
      { checkIn, checkOut, guests },
      {
        onSuccess: (data: { results?: SearchResult[]; available?: SearchResult[] }) => {
          setResults(data.results || data.available || []);
          setStep("results");
        },
      }
    );
  };

  const handleBookNow = (result: SearchResult) => {
    setSelectedResult(result);
    holdMutation.mutate({
      ratePlanId: result.ratePlan.id,
      checkIn,
      checkOut,
    });
  };

  const handleDetailsComplete = () => {
    if (hasUpsells) {
      setStep("addons");
    } else {
      setStep("payment");
    }
  };

  const handleAddonsComplete = (selections: Array<{ itemId: number; quantity: number }>) => {
    setUpsellSelections(selections);
    setStep("payment");
  };

  // Initialize Stripe PaymentIntent when the guest reaches the payment step.
  useEffect(() => {
    if (step !== "payment" || !hold || clientSecret || checkoutMutation.isPending) return;
    const activeUpsells = upsellSelections.filter((s) => s.quantity > 0);
    checkoutMutation.mutate({
      holdId: hold.id,
      guestName,
      guestEmail,
      guestPhone: guestPhone || undefined,
      upsellSelections: activeUpsells.length > 0 ? activeUpsells : undefined,
      source: prefillSource || undefined,
    });
  }, [step, hold]);

  // Handle 3DS redirect returns — Stripe appends ?payment_intent=X&redirect_status=succeeded to the URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentIntentId = params.get("payment_intent");
    const redirectStatus = params.get("redirect_status");
    if (paymentIntentId && redirectStatus === "succeeded" && hold && !bookingConfirmed) {
      confirmMutation.mutate({ holdId: hold.id, paymentIntentId });
    }
  }, [hold]);

  const isAutoSearching = prefillCheckIn && prefillCheckOut && (searchMutation.isPending || holdMutation.isPending);

  if (pageLoading || isAutoSearching) {
    return (
      <div className="min-h-screen bg-white" data-testid="booking-loading">
        <div className="max-w-4xl mx-auto p-6">
          <Skeleton className="h-10 w-64 mb-4" />
          <Skeleton className="h-64 w-full rounded-md mb-6" />
          <Skeleton className="h-12 w-full mb-3" />
          <Skeleton className="h-12 w-full mb-3" />
          <Skeleton className="h-12 w-48" />
          {isAutoSearching && (
            <p className="text-center text-muted-foreground mt-4">Preparing your booking...</p>
          )}
        </div>
      </div>
    );
  }

  if (pageError || !pageData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="booking-error">
        <Card>
          <CardContent className="p-8 text-center">
            <h2 className="text-xl font-semibold mb-2">Booking Page Not Found</h2>
            <p className="text-muted-foreground">This booking page may not exist or is no longer active.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { page } = pageData;

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <header
        className="border-b"
        data-testid="booking-header"
        style={{ borderColor: `${primaryColor}20` }}
      >
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-3 flex-wrap">
          <div
            className="w-8 h-8 rounded-md flex items-center justify-center text-white font-bold text-sm"
            style={{ backgroundColor: primaryColor }}
          >
            {(page.organizationName || page.title || "B").charAt(0).toUpperCase()}
          </div>
          <span className="font-semibold text-lg" data-testid="text-org-name">
            {page.organizationName || page.title}
          </span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {step === "search" && (
          <SearchStep
            page={page}
            primaryColor={primaryColor}
            checkIn={checkIn}
            checkOut={checkOut}
            guests={guests}
            onCheckInChange={setCheckIn}
            onCheckOutChange={setCheckOut}
            onGuestsChange={setGuests}
            onSearch={handleSearch}
            isSearching={searchMutation.isPending}
          />
        )}

        {step === "results" && (
          <ResultsStep
            results={results}
            checkIn={checkIn}
            checkOut={checkOut}
            guests={guests}
            primaryColor={primaryColor}
            apartmentPhotos={pageData.apartmentPhotos}
            onBack={() => setStep("search")}
            onBookNow={handleBookNow}
            isHolding={holdMutation.isPending}
          />
        )}

        {step === "details" && selectedResult && hold && (
          <DetailsStep
            result={selectedResult}
            hold={hold}
            checkIn={checkIn}
            checkOut={checkOut}
            countdown={countdown}
            primaryColor={primaryColor}
            apartmentPhotos={pageData.apartmentPhotos}
            guestName={guestName}
            guestEmail={guestEmail}
            guestPhone={guestPhone}
            onGuestNameChange={setGuestName}
            onGuestEmailChange={setGuestEmail}
            onGuestPhoneChange={setGuestPhone}
            onBack={() => setStep("results")}
            onProceed={handleDetailsComplete}
          />
        )}

        {step === "addons" && selectedResult && hold && (
          <AddonsStep
            upsellItems={upsellItems}
            selectedResult={selectedResult}
            hold={hold}
            checkIn={checkIn}
            checkOut={checkOut}
            primaryColor={primaryColor}
            countdown={countdown}
            initialSelections={upsellSelections}
            onBack={() => setStep("details")}
            onContinue={handleAddonsComplete}
          />
        )}

        {step === "payment" && selectedResult && hold && (
          <PaymentStep
            result={selectedResult}
            hold={hold}
            checkIn={checkIn}
            checkOut={checkOut}
            guestName={guestName}
            guestEmail={guestEmail}
            primaryColor={primaryColor}
            bookingConfirmed={bookingConfirmed}
            upsellSelections={upsellSelections}
            upsellItems={upsellItems}
            clientSecret={clientSecret}
            connectedAccountId={connectedAccountId}
            isInitializing={checkoutMutation.isPending}
            isConfirming={confirmMutation.isPending}
            paymentSetupError={paymentSetupError}
            onPaymentSuccess={(paymentIntentId) =>
              confirmMutation.mutate({ holdId: hold.id, paymentIntentId })
            }
            onBack={() => hasUpsells ? setStep("addons") : setStep("details")}
          />
        )}
      </main>

      <footer className="border-t mt-12 py-6 text-center text-sm text-muted-foreground">
        <p>Powered by Staio</p>
      </footer>
    </div>
  );
}

function SearchStep({
  page,
  primaryColor,
  checkIn,
  checkOut,
  guests,
  onCheckInChange,
  onCheckOutChange,
  onGuestsChange,
  onSearch,
  isSearching,
}: {
  page: PageData["page"];
  primaryColor: string;
  checkIn: string;
  checkOut: string;
  guests: number;
  onCheckInChange: (v: string) => void;
  onCheckOutChange: (v: string) => void;
  onGuestsChange: (v: number) => void;
  onSearch: () => void;
  isSearching: boolean;
}) {
  const handleCheckInChange = (val: string) => {
    onCheckInChange(val);
    if (!checkOut || val >= checkOut) {
      onCheckOutChange(getDefaultCheckout(val));
    }
  };

  return (
    <div data-testid="step-search">
      {page.heroImage && (
        <div className="relative rounded-md overflow-hidden mb-8 aspect-[16/7]">
          <img
            src={page.heroImage}
            alt={page.title}
            className="w-full h-full object-cover"
            data-testid="img-hero"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
            <h1 className="text-3xl font-bold mb-1" data-testid="text-page-title">{page.title}</h1>
            {page.description && (
              <p className="text-white/90 text-base max-w-xl" data-testid="text-page-description">
                {page.description}
              </p>
            )}
          </div>
        </div>
      )}

      {!page.heroImage && (
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2" data-testid="text-page-title">{page.title}</h1>
          {page.description && (
            <p className="text-muted-foreground text-base max-w-xl" data-testid="text-page-description">
              {page.description}
            </p>
          )}
        </div>
      )}

      <Card>
        <CardContent className="p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Search className="w-5 h-5" style={{ color: primaryColor }} />
            Find your perfect stay
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
            <div>
              <Label className="text-sm font-medium mb-1.5 block">
                Check-in
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal truncate",
                      !checkIn && "text-muted-foreground"
                    )}
                    data-testid="input-checkin"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {checkIn ? format(parse(checkIn, "yyyy-MM-dd", new Date()), "MMM d, yyyy") : "Select date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={checkIn ? parse(checkIn, "yyyy-MM-dd", new Date()) : undefined}
                    onSelect={(date) => {
                      if (date) {
                        handleCheckInChange(format(date, "yyyy-MM-dd"));
                      }
                    }}
                    disabled={(date) => date < startOfDay(new Date())}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label className="text-sm font-medium mb-1.5 block">
                Check-out
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal truncate",
                      !checkOut && "text-muted-foreground"
                    )}
                    data-testid="input-checkout"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {checkOut ? format(parse(checkOut, "yyyy-MM-dd", new Date()), "MMM d, yyyy") : "Select date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={checkOut ? parse(checkOut, "yyyy-MM-dd", new Date()) : undefined}
                    onSelect={(date) => {
                      if (date) {
                        onCheckOutChange(format(date, "yyyy-MM-dd"));
                      }
                    }}
                    disabled={(date) => {
                      const minDate = checkIn ? addDays(parse(checkIn, "yyyy-MM-dd", new Date()), 1) : startOfDay(new Date());
                      return date < minDate;
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label htmlFor="guests" className="text-sm font-medium mb-1.5 block">
                Guests
              </Label>
              <div className="relative">
                <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <Input
                  id="guests"
                  type="number"
                  value={guests}
                  min={1}
                  max={20}
                  onChange={(e) => onGuestsChange(parseInt(e.target.value) || 1)}
                  className="pl-10"
                  data-testid="input-guests"
                />
              </div>
            </div>
          </div>

          {page.minStay && (
            <p className="text-xs text-muted-foreground mb-4">
              Minimum stay: {page.minStay} night{page.minStay > 1 ? "s" : ""}
              {page.maxStay ? ` · Maximum stay: ${page.maxStay} nights` : ""}
            </p>
          )}

          <Button
            onClick={onSearch}
            disabled={!checkIn || !checkOut || isSearching}
            className="w-full sm:w-auto text-white border-transparent"
            style={{ backgroundColor: primaryColor }}
            data-testid="button-search"
          >
            {isSearching ? (
              "Searching..."
            ) : (
              <>
                <Search className="w-4 h-4 mr-2" />
                Search Availability
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function ResultsStep({
  results,
  checkIn,
  checkOut,
  guests,
  primaryColor,
  apartmentPhotos,
  onBack,
  onBookNow,
  isHolding,
}: {
  results: SearchResult[];
  checkIn: string;
  checkOut: string;
  guests: number;
  primaryColor: string;
  apartmentPhotos?: Record<number, ApartmentPhotoData[]>;
  onBack: () => void;
  onBookNow: (r: SearchResult) => void;
  isHolding: boolean;
}) {
  return (
    <div data-testid="step-results">
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-muted-foreground mb-4 hover-elevate rounded-md px-2 py-1"
        data-testid="button-back-search"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to search
      </button>

      <div className="flex items-center gap-2 flex-wrap mb-6">
        <h2 className="text-xl font-semibold">Available stays</h2>
        <Badge variant="secondary" className="text-xs">
          {formatDate(checkIn)} - {formatDate(checkOut)}
        </Badge>
        <Badge variant="secondary" className="text-xs">
          {guests} guest{guests > 1 ? "s" : ""}
        </Badge>
      </div>

      {results.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <MapPin className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
            <h3 className="font-semibold mb-1">No availability</h3>
            <p className="text-muted-foreground text-sm">
              No apartments are available for the selected dates. Try adjusting your dates.
            </p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {results.map((result, idx) => {
          const photos = getPhotosForApartment(result.apartment, apartmentPhotos);
          return (
            <Card key={idx} className="overflow-visible" data-testid={`card-result-${idx}`}>
              <div className="overflow-hidden rounded-t-md">
                <PhotoGallery
                  photos={photos}
                  fallbackImage={result.apartment.heroImage}
                  altText={result.apartment.name}
                  aspectClass="aspect-[16/10]"
                  testIdPrefix={`gallery-result-${idx}`}
                />
              </div>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-2 flex-wrap mb-2">
                  <h3 className="font-semibold text-lg" data-testid={`text-apartment-name-${idx}`}>
                    {result.apartment.name}
                  </h3>
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Star className="w-4 h-4" style={{ color: primaryColor }} />
                    <span>New</span>
                  </div>
                </div>

                <div className="flex items-center gap-3 text-sm text-muted-foreground mb-3 flex-wrap">
                  {result.apartment.bedrooms != null && (
                    <span className="flex items-center gap-1">
                      <BedDouble className="w-4 h-4" />
                      {result.apartment.bedrooms} bedroom{result.apartment.bedrooms !== 1 ? "s" : ""}
                    </span>
                  )}
                  <span>{result.nights} night{result.nights !== 1 ? "s" : ""}</span>
                </div>

                <div className="flex items-center gap-1 mb-1">
                  <span className="text-lg font-bold" data-testid={`text-nightly-rate-${idx}`}>
                    {formatCurrency(result.ratePlan.baseNightlyRate)}
                  </span>
                  <span className="text-sm text-muted-foreground">/ night</span>
                </div>

                {result.cleaningFee != null && Number(result.cleaningFee) > 0 && (
                  <p className="text-xs text-muted-foreground mb-1">
                    + {formatCurrency(result.cleaningFee)} cleaning fee
                  </p>
                )}

                {result.taxRate && Number(result.taxRate) > 0 && (
                  <p className="text-xs text-muted-foreground mb-3" data-testid={`text-tax-label-${idx}`}>
                    + <span data-testid={`text-tax-amount-${idx}`}>{formatCurrency(result.taxAmount)}</span> {result.taxName} ({result.taxRate}%)
                  </p>
                )}

                <div className="flex items-center justify-between gap-2 flex-wrap pt-3 border-t">
                  <div>
                    <span className="text-sm text-muted-foreground">Total</span>
                    <p className="text-xl font-bold" data-testid={`text-total-price-${idx}`}>
                      {formatCurrency(result.totalAmount)}
                    </p>
                  </div>
                  <Button
                    onClick={() => onBookNow(result)}
                    disabled={isHolding}
                    className="text-white border-transparent"
                    style={{ backgroundColor: primaryColor }}
                    data-testid={`button-book-now-${idx}`}
                  >
                    {isHolding ? "Reserving..." : "Book Now"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function DetailsStep({
  result,
  hold,
  checkIn,
  checkOut,
  countdown,
  primaryColor,
  apartmentPhotos,
  guestName,
  guestEmail,
  guestPhone,
  onGuestNameChange,
  onGuestEmailChange,
  onGuestPhoneChange,
  onBack,
  onProceed,
}: {
  result: SearchResult;
  hold: HoldData;
  checkIn: string;
  checkOut: string;
  countdown: string;
  primaryColor: string;
  apartmentPhotos?: Record<number, ApartmentPhotoData[]>;
  guestName: string;
  guestEmail: string;
  guestPhone: string;
  onGuestNameChange: (v: string) => void;
  onGuestEmailChange: (v: string) => void;
  onGuestPhoneChange: (v: string) => void;
  onBack: () => void;
  onProceed: () => void;
}) {
  const isExpired = countdown === "Expired";
  const photos = getPhotosForApartment(result.apartment, apartmentPhotos);

  return (
    <div data-testid="step-details">
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-muted-foreground mb-4 hover-elevate rounded-md px-2 py-1"
        data-testid="button-back-results"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to results
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <h2 className="text-xl font-semibold mb-1">Complete your booking</h2>
          <p className="text-sm text-muted-foreground mb-5">
            Fill in your details to confirm the reservation.
          </p>

          <Card>
            <CardContent className="p-6 space-y-4">
              <div>
                <Label htmlFor="guestName" className="text-sm font-medium mb-1.5 block">
                  Full Name *
                </Label>
                <Input
                  id="guestName"
                  value={guestName}
                  onChange={(e) => onGuestNameChange(e.target.value)}
                  placeholder="John Smith"
                  data-testid="input-guest-name"
                />
              </div>
              <div>
                <Label htmlFor="guestEmail" className="text-sm font-medium mb-1.5 block">
                  Email Address *
                </Label>
                <Input
                  id="guestEmail"
                  type="email"
                  value={guestEmail}
                  onChange={(e) => onGuestEmailChange(e.target.value)}
                  placeholder="john@example.com"
                  data-testid="input-guest-email"
                />
              </div>
              <div>
                <Label htmlFor="guestPhone" className="text-sm font-medium mb-1.5 block">
                  Phone Number
                </Label>
                <Input
                  id="guestPhone"
                  type="tel"
                  value={guestPhone}
                  onChange={(e) => onGuestPhoneChange(e.target.value)}
                  placeholder="+1 (555) 000-0000"
                  data-testid="input-guest-phone"
                />
              </div>

              <Button
                onClick={onProceed}
                disabled={!guestName || !guestEmail || isExpired}
                className="w-full text-white border-transparent mt-2"
                style={{ backgroundColor: primaryColor }}
                data-testid="button-proceed-payment"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Proceed to Payment
              </Button>
            </CardContent>
          </Card>
        </div>

        <div>
          <Card>
            <CardContent className="p-5">
              <div
                className={`flex items-center gap-2 mb-4 text-sm font-medium rounded-md px-3 py-2 ${
                  isExpired
                    ? "bg-destructive/10 text-destructive"
                    : "text-white"
                }`}
                style={!isExpired ? { backgroundColor: primaryColor } : undefined}
                data-testid="text-countdown"
              >
                <Clock className="w-4 h-4" />
                {isExpired ? "Hold expired" : `Hold expires in ${countdown}`}
              </div>

              <div className="mb-4">
                <PhotoGallery
                  photos={photos}
                  fallbackImage={result.apartment.heroImage}
                  altText={result.apartment.name}
                  aspectClass="aspect-[16/10]"
                  testIdPrefix="gallery-details"
                />
              </div>

              <h3 className="font-semibold mb-3" data-testid="text-summary-apartment">
                {result.apartment.name}
              </h3>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground">Check-in</span>
                  <span className="font-medium">{formatDate(checkIn)}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground">Check-out</span>
                  <span className="font-medium">{formatDate(checkOut)}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground">Duration</span>
                  <span className="font-medium">
                    {result.nights} night{result.nights !== 1 ? "s" : ""}
                  </span>
                </div>

                <hr className="my-2" />

                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground">
                    {formatCurrency(result.ratePlan.baseNightlyRate)} x {result.nights} nights
                  </span>
                  <span>{formatCurrency(result.totalNightly)}</span>
                </div>

                {result.cleaningFee != null && Number(result.cleaningFee) > 0 && (
                  <div className="flex justify-between gap-2">
                    <span className="text-muted-foreground">Cleaning fee</span>
                    <span>{formatCurrency(result.cleaningFee)}</span>
                  </div>
                )}

                {result.taxRate && Number(result.taxRate) > 0 && (
                  <div className="flex justify-between gap-2" data-testid="text-tax-label">
                    <span className="text-muted-foreground">{result.taxName} ({result.taxRate}%)</span>
                    <span data-testid="text-tax-amount">{formatCurrency(result.taxAmount)}</span>
                  </div>
                )}

                <hr className="my-2" />

                <div className="flex justify-between gap-2 font-bold text-base">
                  <span>Total</span>
                  <span data-testid="text-summary-total">{formatCurrency(result.totalAmount)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function AddonsStep({
  upsellItems,
  selectedResult,
  hold,
  checkIn,
  checkOut,
  primaryColor,
  countdown,
  initialSelections,
  onBack,
  onContinue,
}: {
  upsellItems: UpsellItemData[];
  selectedResult: SearchResult;
  hold: HoldData;
  checkIn: string;
  checkOut: string;
  primaryColor: string;
  countdown: string;
  initialSelections: Array<{ itemId: number; quantity: number }>;
  onBack: () => void;
  onContinue: (selections: Array<{ itemId: number; quantity: number }>) => void;
}) {
  const [quantities, setQuantities] = useState<Record<number, number>>(() => {
    const map: Record<number, number> = {};
    upsellItems.forEach((item) => {
      const existing = initialSelections.find((s) => s.itemId === item.id);
      map[item.id] = existing ? existing.quantity : 0;
    });
    return map;
  });

  const isExpired = countdown === "Expired";

  const updateQuantity = (itemId: number, delta: number) => {
    setQuantities((prev) => ({
      ...prev,
      [itemId]: Math.max(0, (prev[itemId] || 0) + delta),
    }));
  };

  const accommodationTotal = parseFloat(selectedResult.totalAmount);
  const upsellTotal = upsellItems.reduce((sum, item) => {
    return sum + (quantities[item.id] || 0) * parseFloat(item.price);
  }, 0);
  const grandTotal = accommodationTotal + upsellTotal;

  const handleContinue = () => {
    const selections = Object.entries(quantities)
      .filter(([, qty]) => qty > 0)
      .map(([id, qty]) => ({ itemId: parseInt(id), quantity: qty }));
    onContinue(selections);
  };

  return (
    <div data-testid="step-addons">
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-muted-foreground mb-4 hover-elevate rounded-md px-2 py-1"
        data-testid="button-back-details"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Details
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <h2 className="text-xl font-semibold mb-1 flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" style={{ color: primaryColor }} />
            Enhance your stay
          </h2>
          <p className="text-sm text-muted-foreground mb-5">
            Add extras to make your stay even more enjoyable.
          </p>

          <div className="space-y-4">
            {upsellItems.map((item) => (
              <Card key={item.id} className="overflow-visible" data-testid={`card-upsell-${item.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="font-semibold" data-testid={`text-upsell-name-${item.id}`}>
                          {item.name}
                        </h3>
                        <Badge variant="secondary" className="text-xs">
                          {getCategoryIcon(item.category)}
                          <span className="ml-1">{getCategoryLabel(item.category)}</span>
                        </Badge>
                      </div>
                      {item.description && (
                        <p className="text-sm text-muted-foreground mb-2" data-testid={`text-upsell-desc-${item.id}`}>
                          {item.description}
                        </p>
                      )}
                      <p className="text-base font-bold" data-testid={`text-upsell-price-${item.id}`}>
                        {formatCurrency(item.price)}
                        <span className="text-sm font-normal text-muted-foreground ml-1">
                          / {item.currency || "USD"}
                        </span>
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => updateQuantity(item.id, -1)}
                        disabled={(quantities[item.id] || 0) <= 0}
                        data-testid={`button-upsell-minus-${item.id}`}
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span
                        className="w-8 text-center font-semibold text-base"
                        data-testid={`text-upsell-qty-${item.id}`}
                      >
                        {quantities[item.id] || 0}
                      </span>
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => updateQuantity(item.id, 1)}
                        data-testid={`button-upsell-plus-${item.id}`}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        <div>
          <Card>
            <CardContent className="p-5">
              <div
                className={`flex items-center gap-2 mb-4 text-sm font-medium rounded-md px-3 py-2 ${
                  isExpired
                    ? "bg-destructive/10 text-destructive"
                    : "text-white"
                }`}
                style={!isExpired ? { backgroundColor: primaryColor } : undefined}
                data-testid="text-addons-countdown"
              >
                <Clock className="w-4 h-4" />
                {isExpired ? "Hold expired" : `Hold expires in ${countdown}`}
              </div>

              <h3 className="font-semibold mb-3" data-testid="text-addons-apartment">
                {selectedResult.apartment.name}
              </h3>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground">
                    {formatDate(checkIn)} - {formatDate(checkOut)}
                  </span>
                </div>

                <hr className="my-2" />

                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground">Accommodation</span>
                  <span>{formatCurrency(selectedResult.totalAmount)}</span>
                </div>

                {selectedResult.taxRate && Number(selectedResult.taxRate) > 0 && (
                  <div className="flex justify-between gap-2 text-xs" data-testid="text-tax-label">
                    <span className="text-muted-foreground">incl. {selectedResult.taxName} ({selectedResult.taxRate}%)</span>
                    <span data-testid="text-tax-amount">{formatCurrency(selectedResult.taxAmount)}</span>
                  </div>
                )}

                {upsellItems.map((item) => {
                  const qty = quantities[item.id] || 0;
                  if (qty === 0) return null;
                  return (
                    <div key={item.id} className="flex justify-between gap-2" data-testid={`text-addon-line-${item.id}`}>
                      <span className="text-muted-foreground">
                        {item.name} x{qty}
                      </span>
                      <span>{formatCurrency(parseFloat(item.price) * qty)}</span>
                    </div>
                  );
                })}

                <hr className="my-2" />

                <div className="flex justify-between gap-2 font-bold text-base">
                  <span>Grand Total</span>
                  <span data-testid="text-addons-grand-total">{formatCurrency(grandTotal)}</span>
                </div>
              </div>

              <Button
                onClick={handleContinue}
                disabled={isExpired}
                className="w-full text-white border-transparent mt-4"
                style={{ backgroundColor: primaryColor }}
                data-testid="button-continue-payment"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Continue to Payment
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function CheckoutForm({
  grandTotal,
  primaryColor,
  isConfirming,
  onPaymentSuccess,
}: {
  grandTotal: number;
  primaryColor: string;
  isConfirming: boolean;
  onPaymentSuccess: (paymentIntentId: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [elementsReady, setElementsReady] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements || !elementsReady) return;
    setIsSubmitting(true);
    setPaymentError(null);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: "if_required",
    });

    if (error) {
      setPaymentError(error.message ?? "Payment failed. Please try again.");
      setIsSubmitting(false);
      return;
    }

    if (paymentIntent?.status === "succeeded") {
      onPaymentSuccess(paymentIntent.id);
    } else {
      setPaymentError("Payment was not completed. Please try again.");
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement onReady={() => setElementsReady(true)} />
      {paymentError && (
        <p className="text-sm text-red-600">{paymentError}</p>
      )}
      <Button
        type="submit"
        disabled={!stripe || !elementsReady || isSubmitting || isConfirming}
        className="w-full text-white border-transparent"
        style={{ backgroundColor: primaryColor }}
        data-testid="button-confirm-payment"
      >
        {isSubmitting || isConfirming ? "Processing..." : `Pay ${new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(grandTotal)}`}
      </Button>
    </form>
  );
}

function StripePaymentCard({
  grandTotal,
  primaryColor,
  clientSecret,
  connectedAccountId,
  isInitializing,
  isConfirming,
  paymentSetupError,
  onPaymentSuccess,
}: {
  grandTotal: number;
  primaryColor: string;
  clientSecret: string | null;
  connectedAccountId: string | null;
  isInitializing: boolean;
  isConfirming: boolean;
  paymentSetupError: string | null;
  onPaymentSuccess: (paymentIntentId: string) => void;
}) {
  const publishableKey = import.meta.env.VITE_STRIPE_PUBLIC_KEY as string | undefined;

  const stripePromise = useMemo(() => {
    if (!publishableKey || !connectedAccountId) return null;
    return loadStripe(publishableKey, { stripeAccount: connectedAccountId });
  }, [publishableKey, connectedAccountId]);

  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-muted-foreground" />
          <h3 className="font-semibold">Payment</h3>
        </div>

        {!publishableKey && (
          <p className="text-sm text-red-600">
            Payment is not configured. Please contact the property manager.
          </p>
        )}

        {paymentSetupError && (
          <p className="text-sm text-red-600">{paymentSetupError}</p>
        )}

        {isInitializing && (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        )}

        {clientSecret && stripePromise && (
          <Elements
            stripe={stripePromise}
            options={{ clientSecret, appearance: { theme: "stripe" } }}
          >
            <CheckoutForm
              grandTotal={grandTotal}
              primaryColor={primaryColor}
              isConfirming={isConfirming}
              onPaymentSuccess={onPaymentSuccess}
            />
          </Elements>
        )}
      </CardContent>
    </Card>
  );
}

function PaymentStep({
  result,
  hold,
  checkIn,
  checkOut,
  guestName,
  guestEmail,
  primaryColor,
  bookingConfirmed,
  upsellSelections,
  upsellItems,
  clientSecret,
  connectedAccountId,
  isInitializing,
  isConfirming,
  paymentSetupError,
  onPaymentSuccess,
  onBack,
}: {
  result: SearchResult;
  hold: HoldData;
  checkIn: string;
  checkOut: string;
  guestName: string;
  guestEmail: string;
  primaryColor: string;
  bookingConfirmed: boolean;
  upsellSelections: Array<{ itemId: number; quantity: number }>;
  upsellItems: UpsellItemData[];
  clientSecret: string | null;
  connectedAccountId: string | null;
  isInitializing: boolean;
  isConfirming: boolean;
  paymentSetupError: string | null;
  onPaymentSuccess: (paymentIntentId: string) => void;
  onBack: () => void;
}) {
  const activeUpsells = upsellSelections.filter((s) => s.quantity > 0);
  const accommodationTotal = parseFloat(result.totalAmount);
  const upsellTotal = activeUpsells.reduce((sum, sel) => {
    const item = upsellItems.find((u) => u.id === sel.itemId);
    return sum + (item ? parseFloat(item.price) * sel.quantity : 0);
  }, 0);
  const grandTotal = accommodationTotal + upsellTotal;

  if (bookingConfirmed) {
    return (
      <div className="max-w-lg mx-auto text-center py-12" data-testid="step-confirmation">
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
          style={{ backgroundColor: `${primaryColor}15` }}
        >
          <CheckCircle className="w-8 h-8" style={{ color: primaryColor }} />
        </div>
        <h2 className="text-2xl font-bold mb-2">Booking Confirmed</h2>
        <p className="text-muted-foreground mb-6">
          Your reservation has been confirmed. A confirmation email will be sent to{" "}
          <span className="font-medium text-foreground">{guestEmail}</span>.
        </p>

        <Card>
          <CardContent className="p-5 text-left space-y-3 text-sm">
            <div className="flex justify-between gap-2">
              <span className="text-muted-foreground">Apartment</span>
              <span className="font-medium" data-testid="text-confirm-apartment">{result.apartment.name}</span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-muted-foreground">Guest</span>
              <span className="font-medium" data-testid="text-confirm-guest">{guestName}</span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-muted-foreground">Check-in</span>
              <span className="font-medium">{formatDate(checkIn)}</span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-muted-foreground">Check-out</span>
              <span className="font-medium">{formatDate(checkOut)}</span>
            </div>
            <hr />
            <div className="flex justify-between gap-2">
              <span className="text-muted-foreground">Accommodation</span>
              <span>{formatCurrency(result.totalAmount)}</span>
            </div>
            {result.taxRate && Number(result.taxRate) > 0 && (
              <div className="flex justify-between gap-2 text-xs" data-testid="text-tax-label">
                <span className="text-muted-foreground">incl. {result.taxName} ({result.taxRate}%)</span>
                <span data-testid="text-tax-amount">{formatCurrency(result.taxAmount)}</span>
              </div>
            )}
            {activeUpsells.map((sel) => {
              const item = upsellItems.find((u) => u.id === sel.itemId);
              if (!item) return null;
              return (
                <div key={sel.itemId} className="flex justify-between gap-2" data-testid={`text-confirm-upsell-${sel.itemId}`}>
                  <span className="text-muted-foreground">{item.name} x{sel.quantity}</span>
                  <span>{formatCurrency(parseFloat(item.price) * sel.quantity)}</span>
                </div>
              );
            })}
            <hr />
            <div className="flex justify-between gap-2 font-bold text-base">
              <span>Total Paid</span>
              <span data-testid="text-confirm-total">{formatCurrency(grandTotal)}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="step-payment">
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-muted-foreground mb-4 hover-elevate rounded-md px-2 py-1"
        data-testid="button-back-addons"
      >
        <ArrowLeft className="w-4 h-4" />
        Back
      </button>

      <div className="max-w-lg mx-auto">
        <h2 className="text-xl font-semibold mb-1">Payment</h2>
        <p className="text-sm text-muted-foreground mb-5">
          Complete your payment to confirm the reservation.
        </p>

        <Card className="mb-5">
          <CardContent className="p-5 text-sm space-y-2">
            <div className="flex justify-between gap-2">
              <span className="text-muted-foreground">Apartment</span>
              <span className="font-medium">{result.apartment.name}</span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-muted-foreground">Dates</span>
              <span className="font-medium">
                {formatDate(checkIn)} - {formatDate(checkOut)}
              </span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-muted-foreground">Guest</span>
              <span className="font-medium">{guestName}</span>
            </div>
            <hr />
            <div className="flex justify-between gap-2">
              <span className="text-muted-foreground">Accommodation</span>
              <span>{formatCurrency(result.totalAmount)}</span>
            </div>
            {result.taxRate && Number(result.taxRate) > 0 && (
              <div className="flex justify-between gap-2 text-xs" data-testid="text-tax-label">
                <span className="text-muted-foreground">incl. {result.taxName} ({result.taxRate}%)</span>
                <span data-testid="text-tax-amount">{formatCurrency(result.taxAmount)}</span>
              </div>
            )}
            {activeUpsells.map((sel) => {
              const item = upsellItems.find((u) => u.id === sel.itemId);
              if (!item) return null;
              return (
                <div key={sel.itemId} className="flex justify-between gap-2" data-testid={`text-payment-upsell-${sel.itemId}`}>
                  <span className="text-muted-foreground">{item.name} x{sel.quantity}</span>
                  <span>{formatCurrency(parseFloat(item.price) * sel.quantity)}</span>
                </div>
              );
            })}
            <hr />
            <div className="flex justify-between gap-2 font-bold text-base">
              <span>Total</span>
              <span data-testid="text-payment-total">{formatCurrency(grandTotal)}</span>
            </div>
          </CardContent>
        </Card>

        <StripePaymentCard
          grandTotal={grandTotal}
          primaryColor={primaryColor}
          clientSecret={clientSecret}
          connectedAccountId={connectedAccountId}
          isInitializing={isInitializing}
          isConfirming={isConfirming}
          paymentSetupError={paymentSetupError}
          onPaymentSuccess={onPaymentSuccess}
        />
      </div>
    </div>
  );
}
